import React, { useCallback, useEffect, useState } from "react";
import { getAuth, sendEmailVerification, signOut } from "firebase/auth";
import { useUser } from "../shared/UserContext";
import logo from "../../images/logo.svg";
import "./auth.css";

// Shown to a signed-in teacher whose email isn't verified yet (password sign-ups
// only — Google addresses arrive verified). Blocks the app until they click the
// link, and auto-detects the click so they don't have to hit a button.
export default function VerifyEmail() {
  const { user, refreshEmailVerified } = useUser();
  const auth = getAuth();
  const email = user?.email || auth.currentUser?.email || "your email address";

  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const ok = await refreshEmailVerified();
      if (!ok) setMessage("Not verified yet — click the link in the email, then try again.");
    } finally {
      setChecking(false);
    }
  }, [refreshEmailVerified]);

  // Teachers usually click the link in a different tab; poll so the gate drops on
  // its own once they do.
  useEffect(() => {
    const id = setInterval(() => { refreshEmailVerified(); }, 5000);
    return () => clearInterval(id);
  }, [refreshEmailVerified]);

  const resend = async () => {
    setSending(true);
    setMessage("");
    try {
      await sendEmailVerification(auth.currentUser);
      setMessage("Verification email sent. Check your inbox (and spam folder).");
    } catch {
      setMessage("Couldn't resend just yet — wait a moment and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="auth-card">
      <div className="auth-head">
        <img src={logo} className="auth-logo" alt="classBOON" />
        <h1 className="auth-title">Verify your email</h1>
        <p className="auth-sub">
          We sent a verification link to <strong>{email}</strong>. Click it to
          activate your classroom, then come back here.
        </p>
      </div>

      <button type="button" className="auth-submit" onClick={check} disabled={checking}>
        {checking ? "Checking…" : "I've verified — continue"}
      </button>

      <div className="auth-divider">didn't get it?</div>
      <button type="button" className="auth-linkbtn" onClick={resend} disabled={sending}>
        {sending ? "Sending…" : "Resend verification email"}
      </button>

      {message && <p className="auth-reset-msg">{message}</p>}

      <div className="auth-foot">
        Wrong account?{" "}
        <button type="button" className="auth-linkbtn" onClick={() => signOut(auth)}>
          Sign out
        </button>
      </div>
    </div>
  );
}
